import _ from 'lodash';
import { useEffect, useState } from 'react';
import {
  TutorialHighlightType,
  TutorialMessageType,
  useTutorialStore,
} from 'react-scale/components/scalers/tasks/tutorial/hooks/useTutorialStore';
import { twMerge } from 'tailwind-merge';

import { Icon, Tooltip } from '@scale/scaleui';
import { getUniqTutorialId } from '@scale/shared/benchmarks/tutorial/helpers';
import { BenchmarkTutorialMode } from '@scale/shared/qm/qmRequestTypes';
import TutorialMessage from 'react-scale/components/scalers/tasks/tutorial/TutorialMessage';
import AcknowledgeControl, { TUTORIAL_MESSAGE_VARIANT_MAP } from './AcknowledgeControl';

interface TutorialFieldContainerProps {
  fieldId: string;
  stepIndex: number;
  response: $FIXME;
  // Prescence of candidateId means we are in a TextCollectionPerResponse step
  candidateId?: string;
}

/**
 * Top level component for managing tutorial state for a field
 */
// eslint-disable-next-line complexity
const TutorialFieldContainer = ({
  response,
  fieldId,
  stepIndex,
  candidateId,
}: TutorialFieldContainerProps) => {
  // Zustand getter from store
  const getTutorialResponseForField = useTutorialStore(state => state.getTutorialResponseForField);
  const setTutorialHighlightStrings = useTutorialStore(state => state.setHighlightStrings);
  const setTutorialHighlightActive = useTutorialStore(state => state.setHighlightActive);
  const setTutorialHighlightFieldId = useTutorialStore(state => state.setHighlightFieldId);
  const setTutorialHighlightType = useTutorialStore(state => state.setHighlightType);
  const tutorialHighlightActive = useTutorialStore(state => state.highlightActive);
  const tutorialHighlightFieldId = useTutorialStore(state => state.highlightFieldId);
  const tutorialHighlightType = useTutorialStore(state => state.highlightType);
  const tutorialMode = useTutorialStore(state => state.tutorialMode);
  const canSubmitScenario = useTutorialStore(state => state.canSubmitScenario);
  const acknowledgedTutorialFields = useTutorialStore(state => state.acknowledgedTutorialFields);
  const setAcknowledgedTutorialFields = useTutorialStore(
    state => state.setAcknowledgedTutorialFields,
  );

  // Tutorial Card state
  const [localTutorialMessage, setLocalTutorialMessage] = useState<string | undefined>(undefined);
  const [localTutorialMessageType, setLocalTutorialMessageType] = useState<
    TutorialMessageType | undefined
  >(undefined);
  const [localTutorialHighlight, setLocalTutorialHighlight] = useState<
    TutorialHighlightType | undefined
  >(undefined);
  const [localTutorialHighlightType, setLocalTutorialHighlightType] = useState<
    TutorialMessageType | undefined
  >(undefined);

  const tutorialId = getUniqTutorialId(stepIndex, fieldId, candidateId);

  // Array exception for categorical fields
  const nonEmptyResponse = Array.isArray(response)
    ? response.length > 0
    : !_.isNil(response) && response !== '' && response !== -1;

  useEffect(() => {
    // We check if the response is not empty and has changed compared to initial value
    if (nonEmptyResponse) {
      // Fetch response
      const tutorialResponse = getTutorialResponseForField(
        fieldId,
        stepIndex,
        response,
        candidateId,
      );

      // If something is nully, we don't set a tutorial response
      if (!tutorialResponse || !tutorialResponse.message || !tutorialResponse.messageType) return;

      // If tutorial message exists, track for checking CB acknowledgement
      if (!(tutorialId in acknowledgedTutorialFields)) {
        const newTutorialFields = { ...acknowledgedTutorialFields, [tutorialId]: false };
        setAcknowledgedTutorialFields(newTutorialFields);
      }

      // Otherwise set which will trigger rendering of tutorial card
      const { message, messageType, highlight } = tutorialResponse;
      setLocalTutorialMessage(message);
      setLocalTutorialMessageType(messageType);

      // If a highlight is provided, set it
      if (!_.isNil(highlight)) {
        setLocalTutorialHighlight(highlight);
        setLocalTutorialHighlightType(messageType);
      }
    } else {
      // If response is empty, clear tutorial message
      setLocalTutorialMessage(undefined);
      setLocalTutorialMessageType(undefined);
    }
  }, [acknowledgedTutorialFields, response]);

  useEffect(() => {
    if (
      tutorialHighlightActive &&
      tutorialHighlightFieldId === tutorialId &&
      localTutorialHighlightType !== tutorialHighlightType
    ) {
      setTutorialHighlightType(localTutorialHighlightType);
    }
  }, [
    localTutorialHighlightType,
    setTutorialHighlightType,
    tutorialHighlightActive,
    tutorialHighlightFieldId,
    tutorialHighlightType,
    tutorialId,
  ]);

  const handleHighlightClick = () => {
    if (!_.isNil(localTutorialHighlight) && tutorialId !== tutorialHighlightFieldId) {
      setTutorialHighlightFieldId(tutorialId);
      setTutorialHighlightStrings(localTutorialHighlight.highlightStrings);
      if (tutorialHighlightType !== localTutorialHighlightType) {
        setTutorialHighlightType(localTutorialHighlightType);
      }
      if (!tutorialHighlightActive) {
        setTutorialHighlightActive(true);
      }
    } else if (tutorialId === tutorialHighlightFieldId) {
      setTutorialHighlightActive(!tutorialHighlightActive);
    }
  };

  const renderHighlightControl = localTutorialHighlight ? (
    <button className="pointer-events-auto cursor-pointer" onClick={() => handleHighlightClick()}>
      {tutorialHighlightActive && tutorialHighlightFieldId === tutorialId ? (
        <Tooltip
          asChild={false}
          text={'Clear Highlight'}
          delayDuration={0}
          target={
            <Icon
              className={twMerge(
                'rounded-lg',
                localTutorialMessageType === TutorialMessageType.ERROR &&
                  'bg-danger-100 hover:bg-danger-200',
                localTutorialMessageType === TutorialMessageType.SUCCESS &&
                  'bg-success-100 hover:bg-success-200',
                localTutorialMessageType === TutorialMessageType.INFO &&
                  'bg-info-100 hover:bg-info-200',
              )}
              icon={['fas', 'eraser']}
              hasBackground
              variant={
                localTutorialMessageType
                  ? _.get(TUTORIAL_MESSAGE_VARIANT_MAP, localTutorialMessageType, 'info')
                  : 'info'
              }
            />
          }
        />
      ) : (
        <Tooltip
          asChild={false}
          text={'Highlight relevant response text'}
          delayDuration={0}
          target={
            <Icon
              className={twMerge(
                'rounded-lg',
                localTutorialMessageType === TutorialMessageType.ERROR &&
                  'bg-danger-100 hover:bg-danger-200',
                localTutorialMessageType === TutorialMessageType.SUCCESS &&
                  'bg-success-100 hover:bg-success-200',
                localTutorialMessageType === TutorialMessageType.INFO &&
                  'bg-info-100 hover:bg-info-200',
              )}
              icon={['fas', 'highlighter-line']}
              hasBackground
              variant={
                localTutorialMessageType
                  ? _.get(TUTORIAL_MESSAGE_VARIANT_MAP, localTutorialMessageType, 'info')
                  : 'info'
              }
            />
          }
        />
      )}
    </button>
  ) : null;

  const displayTutorialMessage: boolean =
    ([BenchmarkTutorialMode.Course, BenchmarkTutorialMode.Instructor].includes(tutorialMode) ||
      (tutorialMode === BenchmarkTutorialMode.Practice && canSubmitScenario)) &&
    nonEmptyResponse &&
    !_.isUndefined(localTutorialMessage) &&
    !_.isUndefined(localTutorialMessageType);

  return (
    <div id={tutorialId} className="scale-theme scale-tailwind scaleui">
      {displayTutorialMessage && (
        <TutorialMessage
          message={localTutorialMessage ?? ''}
          messageType={localTutorialMessageType}
          highlightControl={renderHighlightControl}
          acknowledgeControl={
            <AcknowledgeControl
              messageType={localTutorialMessageType}
              checked={acknowledgedTutorialFields[tutorialId]}
              setChecked={() =>
                setAcknowledgedTutorialFields({ ...acknowledgedTutorialFields, [tutorialId]: true })
              }
            />
          }
        />
      )}
    </div>
  );
};

export default TutorialFieldContainer;
